/**
 * Today — the home screen. Your calendars as a day grid, the day's tasks
 * beside it.
 *
 * Tasks is the whole list, every scope; this is one day, and the question it
 * answers is "what does today actually look like" — meetings take the time,
 * tasks go in what's left. The calendars are the ones connected for Time
 * (Settings › Calendars), read through the same Worker; see lib/calendarDay.ts.
 *
 * Tasks carry a date and no time, so they stay in the sidebar rather than on
 * the grid.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

import { useLogMeeting } from "@/components/CalendarCard";
import { ItemRow, itemProjectId } from "@/components/ItemRow";
import { Dot, GroupHeading, groupTone } from "@/components/atoms";
import { useChrome } from "@/context/ChromeContext";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { useTimer } from "@/context/TimerContext";
import { subtaskCounts, useAllItems, useAllSubtasks, useProjects, useToggleItem } from "@/hooks/useData";
import { useTimeEntries } from "@/hooks/useTime";
import { useDayCalendar, type DayEvent } from "@/lib/calendarDay";
import { itemsById as buildItemsById } from "@/lib/deps";
import { isOverdue, parseDate, scheduledFor, startOfDay } from "@/lib/format";
import { progressCounts } from "@/lib/hierarchy";
import { withLinks } from "@/lib/links";
import { useUnloggedMeetings, type CalendarEvent } from "@/lib/calendarImport";
import { useTodayLogged } from "@/lib/theme";
import { GENERAL_WORK_LABEL, entryDayKey, toIsoLocal } from "@/lib/timeRollup";
import type { Item, Priority, Project, TimeEntry } from "@/lib/types";

import { EntryEditor } from "./Time";

const HOUR = 48;
// The grid always shows at least the working day; an earlier or later event
// widens it rather than falling off the edge.
const MIN_START = 7;
const MIN_END = 19;

/**
 * A calendar's swatch as a block: a light wash of it, text in a deep shade of
 * it, and the colour itself on the edge. Mixed against the theme's own surface
 * and ink, so one swatch reads in both modes.
 */
const tone = (color: string) => ({
  bg: `color-mix(in srgb, ${color} 24%, var(--surface))`,
  ink: `color-mix(in srgb, ${color} 55%, var(--ink))`,
  bar: color,
});

const PRIORITY_WEIGHT: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

export function Today() {
  const navigate = useNavigate();
  const [day, setDay] = useState(() => startOfDay());
  const now = useNow();
  const isToday = toIsoLocal(day) === toIsoLocal(now);
  const shift = (n: number) => setDay((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));

  const cal = useDayCalendar(day);
  const [openEvent, setOpenEvent] = useState<{ e: DayEvent; at: { x: number; y: number } } | null>(null);
  // A different day is a different set of events; the open card goes with it.
  useEffect(() => setOpenEvent(null), [day]);
  const { data: items = [] } = useAllItems();
  const { data: projects = [] } = useProjects();
  const { data: subs = [] } = useAllSubtasks();
  const toggle = useToggleItem();
  const { openItem } = useTaskEditor();

  const projectById = useMemo(() => new Map<string, Project>(projects.map((p) => [p.id, p])), [projects]);
  const byId = useMemo(() => buildItemsById(items), [items]);
  const subCounts = useMemo(() => progressCounts(items, subtaskCounts(subs)), [items, subs]);

  // Overdue only belongs to today: looking at Thursday, last week's slips are
  // not Thursday's tasks.
  const key = toIsoLocal(day);
  // Same set as Tasks › Today: every open item, sub-tasks included.
  const open = items.filter((i) => i.status !== "done");
  const onDay = sortItems(open.filter((i) => {
    const d = parseDate(scheduledFor(i));
    return !!d && toIsoLocal(d) === key;
  }));
  const overdue = isToday ? sortItems(open.filter((i) => isOverdue(i))) : [];
  // The day's own tasks are filed by project, most urgent first within each,
  // so a day spread over three clients reads as three short lists.
  const groups = [
    { title: "Overdue", items: overdue, warn: true, byProject: null },
    { title: isToday ? "Today" : "Scheduled", items: onDay, warn: false, byProject: byProject(onDay, projectById) },
  ].filter((g) => g.items.length > 0);
  const taskCount = overdue.length + onDay.length;

  const meetingMinutes = Math.round(cal.timed.reduce((n, e) => n + (e.end.getTime() - e.start.getTime()) / 60_000, 0));

  // -- The day's logged time ---------------------------------------------------
  const { data: entries = [] } = useTimeEntries();
  // Settings › Time tracking turns it off: the Logged section and the total.
  const [loggedPref] = useTodayLogged();
  const showLogged = loggedPref === "on";
  const timer = useTimer();
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  // In the order they were logged. A manual entry's clock is only when you
  // pressed Log, so this is a list with lengths, never a place on the grid.
  const dayEntries = entries
    .filter((e) => e.end && entryDayKey(e) === key)
    .sort((x, y) => x.start.localeCompare(y.start));
  const unlogged = useUnloggedMeetings(entries, projects, key, now);
  const logMeeting = useLogMeeting(projects);
  const entryLabel = (e: TimeEntry) =>
    e.note?.trim() || (e.item && byId.get(e.item)?.title) || (e.project && projectById.get(e.project)?.name)
    || e.category || GENERAL_WORK_LABEL;
  const running = isToday && timer.running && timer.startedAt
    ? {
      label: timer.meta.note?.trim() || (timer.meta.projectId && projectById.get(timer.meta.projectId)?.name)
        || timer.meta.category || "Ad-hoc",
    }
    : null;
  const loggedMinutes = Math.round(
    (dayEntries.reduce((n, e) => n + (e.durationSeconds || 0), 0) + (running ? timer.seconds : 0)) / 60,
  );

  useChrome({
    title: isToday ? "Today" : day.toLocaleDateString(undefined, { weekday: "long" }),
    subtitle: [
      day.toLocaleDateString(undefined, { weekday: isToday ? "long" : undefined, month: "long", day: "numeric" }),
      cal.connected ? `${cal.timed.length} ${cal.timed.length === 1 ? "meeting" : "meetings"} · ${hm(meetingMinutes)}` : null,
      showLogged && loggedMinutes > 0 ? `${hm(loggedMinutes)} logged` : null,
      `${taskCount} ${taskCount === 1 ? "task" : "tasks"}`,
    ].filter(Boolean).join(" · "),
    actions: (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button onClick={() => shift(-1)} aria-label="Previous day" style={navBtn}>‹</button>
        <button onClick={() => setDay(startOfDay())} style={{ ...navBtn, padding: "6px 12px", opacity: isToday ? 0.55 : 1 }}>Today</button>
        <button onClick={() => shift(1)} aria-label="Next day" style={navBtn}>›</button>
      </div>
    ),
  });

  return (
    <div style={{ height: "100%", minHeight: 0, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 360px)" }}>
      {/* -- The day -------------------------------------------------------- */}
      <section style={{ minHeight: 0, display: "flex", flexDirection: "column", padding: "14px 0 0 18px" }}>
        {cal.connected && cal.calendars.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "0 18px 10px 46px", fontSize: 12, color: "var(--muted)" }}>
            {cal.calendars.map((c) => (
              <span key={c.label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: c.color }} />{c.label}
              </span>
            ))}
            {cal.loading && <span>Loading…</span>}
            {(cal.failure || cal.errors[0]) && (
              <span style={{ color: "var(--warn)" }}>
                Couldn’t read {cal.failure ? "your calendars" : cal.errors[0].label} — {cal.failure ?? cal.errors[0].message}
              </span>
            )}
          </div>
        )}

        {!cal.connected && (
          <div style={{
            display: "flex", alignItems: "center", gap: 12, margin: "0 18px 12px 0", padding: "11px 14px",
            borderRadius: 10, border: "1px dashed var(--line2)", background: "var(--surface2)", fontSize: 12.5, color: "var(--muted)",
          }}>
            <span style={{ flex: 1 }}>No calendars connected. The ones you add for Time show up here as your day.</span>
            <button onClick={() => navigate("/settings#calendars")} style={navBtn}>Connect</button>
          </div>
        )}

        {cal.allDay.length > 0 && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "0 18px 8px 0" }}>
            <span style={{ width: 38, flex: "none", textAlign: "right", fontSize: 11, color: "var(--muted2)", paddingTop: 4 }}>All day</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1, minWidth: 0 }}>
              {cal.allDay.map((e) => (
                <button key={e.id} title={`${e.summary} · ${e.calendarLabel}`}
                  onClick={(ev) => setOpenEvent({ e, at: { x: ev.clientX, y: ev.clientY } })}
                  style={{
                    padding: "3px 9px", borderRadius: 6, fontSize: 12, fontWeight: "var(--fw-medium)", border: "none",
                    background: tone(e.color).bg, color: tone(e.color).ink, cursor: "pointer", fontFamily: "inherit",
                    maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{e.summary}</button>
              ))}
            </div>
          </div>
        )}

        <DayGrid events={cal.timed} now={now} isToday={isToday} dayKey={key}
          onOpen={(e, at) => setOpenEvent({ e, at })} />
      </section>

      {/* -- The day's tasks ------------------------------------------------ */}
      <aside style={{
        minHeight: 0, overflowY: "auto", borderLeft: "1px solid var(--line)", background: "var(--surface)",
        padding: "14px 14px 24px",
      }}>
        {showLogged && (
          <LoggedSection entries={dayEntries} running={running} runningSeconds={timer.seconds}
            totalMinutes={loggedMinutes} meetings={unlogged.meetings} label={entryLabel}
            projectName={(id) => (id ? projectById.get(id)?.name : undefined)}
            onEdit={setEditingEntry} onOpenMeeting={(ev) => setEditingEntry(meetingDraft(ev))}
            onLog={(ev) => logMeeting(ev, ev.projectId)} onDismiss={unlogged.dismiss}
            onOpenTime={() => navigate("/time")} />
        )}
        {groups.length === 0 && (
          <div style={{ padding: "22px 6px", fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5 }}>
            {isToday ? "Nothing due today and nothing overdue." : "Nothing scheduled for this day."}
          </div>
        )}
        {groups.map((g) => (
          <div key={g.title} style={{ marginBottom: 16 }}>
            <GroupHeading label={g.title} count={g.items.length} tone={groupTone(g.title, g.warn)} />
            {g.byProject ? g.byProject.map((pg) => (
              <div key={pg.key} style={{ marginTop: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "0 4px 2px", fontSize: 12, color: "var(--ink2)", fontWeight: "var(--fw-medium)" }}>
                  <Dot color={pg.color} size={7} />
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pg.name}</span>
                  <span className="mono" style={{ fontSize: 10.5, color: "var(--muted2)" }}>{pg.items.length}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {pg.items.map((it) => (
                    <ItemRow key={it.id} item={it} variant="flush" stacked showProject={false} projectById={projectById}
                      onToggle={toggle} onOpen={openItem} itemsById={byId} subCounts={subCounts} />
                  ))}
                </div>
              </div>
            )) : (
              <div style={{ display: "flex", flexDirection: "column", marginTop: 4 }}>
                {g.items.map((it) => (
                  <ItemRow key={it.id} item={it} variant="flush" stacked projectById={projectById}
                    onToggle={toggle} onOpen={openItem} itemsById={byId} subCounts={subCounts} />
                ))}
              </div>
            )}
          </div>
        ))}
      </aside>

      {openEvent && <EventCard event={openEvent.e} at={openEvent.at} onClose={() => setOpenEvent(null)} />}
      {editingEntry && <EntryEditor entry={editingEntry} onClose={() => setEditingEntry(null)} />}
    </div>
  );
}

// ---- Logged time --------------------------------------------------------------

/**
 * The day's time, at the top of the task column: the total, every entry in
 * the order it was logged (click one to correct it), a running timer, and the
 * meetings that are over with nothing logged against them, each one click
 * from an entry. Those meetings are the useful gap: an empty stretch of the
 * day might be work logged later, but a meeting with no entry is usually time
 * that never got billed.
 */
function LoggedSection({ entries, running, runningSeconds, totalMinutes, meetings, label, projectName, onEdit, onOpenMeeting, onLog, onDismiss, onOpenTime }: {
  entries: TimeEntry[];
  running: { label: string } | null;
  runningSeconds: number;
  totalMinutes: number;
  meetings: CalendarEvent[];
  label: (e: TimeEntry) => string;
  projectName: (id?: string) => string | undefined;
  onEdit: (e: TimeEntry) => void;
  /** Open the meeting as an unsaved entry, to change its project or times before logging. */
  onOpenMeeting: (ev: CalendarEvent) => void;
  onLog: (ev: CalendarEvent) => Promise<boolean>;
  onDismiss: (id: string) => void;
  onOpenTime: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const empty = entries.length === 0 && !running && meetings.length === 0;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: "var(--good)" }}>Logged</span>
        <span className="mono" style={{ fontSize: 12, color: "var(--muted2)" }}>{hm(totalMinutes)}</span>
        <span style={{ flex: 1, height: 1, background: "var(--tint2)" }} />
        <button onClick={onOpenTime} style={linkBtn}>Time ›</button>
      </div>

      {empty && <div style={{ padding: "4px 2px", fontSize: 12.5, color: "var(--muted)" }}>Nothing logged yet.</div>}

      {running && (
        <div style={{ ...entryRow, cursor: "default" }} title="Timer running — stop it from the top bar">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...oneLine, fontSize: 13, color: "var(--ink)" }}>
              <span style={{ color: "var(--good)" }}>●</span> {running.label}
            </div>
            <div style={entryMeta}>Running</div>
          </div>
          <span className="mono" style={entryLength}>{hm(Math.round(runningSeconds / 60))}</span>
        </div>
      )}

      {entries.map((e) => (
        <button key={e.id} onClick={() => onEdit(e)} style={entryRow} title="Edit entry">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...oneLine, fontSize: 13, color: "var(--ink)" }}>{label(e)}</div>
            {projectName(e.project) && projectName(e.project) !== label(e) && (
              <div style={entryMeta}>{projectName(e.project)}</div>
            )}
          </div>
          <span className="mono" style={entryLength}>{hm(Math.round(e.durationSeconds / 60))}</span>
        </button>
      ))}

      {meetings.length > 0 && (
        <>
          <div style={{ fontSize: 11.5, color: "var(--muted)", margin: "10px 2px 4px" }}>Meetings not logged</div>
          {meetings.map((ev) => (
            <div key={ev.id} style={{ ...entryRow, cursor: "default", border: "1px dashed var(--line2)", borderRadius: 8, padding: "6px 8px", marginBottom: 4 }}>
              <button onClick={() => onOpenMeeting(ev)} title={`${ev.summary}\nOpen to set the project or times`}
                style={{ flex: 1, minWidth: 0, padding: 0, border: "none", background: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                <div style={{ ...oneLine, fontSize: 13, color: "var(--ink)" }}>{ev.summary}</div>
                <div style={entryMeta}>{hm(ev.durationMinutes)} · {projectName(ev.projectId) ?? "No project"}</div>
              </button>
              <button onClick={() => onDismiss(ev.id)} aria-label={`Dismiss ${ev.summary}`} title="Not billable — stop offering it" style={linkBtn}>✕</button>
              <button disabled={busy === ev.id} onClick={async () => { setBusy(ev.id); await onLog(ev); setBusy(null); }}
                style={{ ...logBtn, opacity: busy === ev.id ? 0.6 : 1 }}>Log</button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * A meeting as an unsaved entry: its own clock, its title as the description
 * and the guessed project, so the entry editor creates it on save. Once saved
 * it matches the meeting and drops off the "not logged" list.
 */
function meetingDraft(ev: CalendarEvent): TimeEntry {
  return {
    id: "", start: ev.start, end: ev.end, durationSeconds: ev.durationMinutes * 60,
    note: ev.summary, project: ev.projectId || undefined, billable: true,
  } as TimeEntry;
}

// ---- The grid ---------------------------------------------------------------

function DayGrid({ events, now, isToday, dayKey, onOpen }: {
  events: DayEvent[]; now: Date; isToday: boolean; dayKey: string;
  onOpen: (e: DayEvent, at: { x: number; y: number }) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const hourOf = (d: Date) => d.getHours() + d.getMinutes() / 60;

  // Events that run past midnight either way are clipped to the day.
  const placed = useMemo(() => layout(events.map((e) => ({
    e,
    a: toIsoLocal(e.start) < dayKey ? 0 : hourOf(e.start),
    b: toIsoLocal(e.end) > dayKey ? 24 : hourOf(e.end),
  }))), [events, dayKey]);

  const startH = Math.min(MIN_START, ...placed.map((p) => Math.floor(p.a)));
  const endH = Math.max(MIN_END, ...placed.map((p) => Math.ceil(p.b)));
  const nowH = hourOf(now);

  // Open on the hour before now, or the start of the morning on another day.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTop = Math.max(0, ((isToday ? nowH - 1 : 8) - startH) * HOUR);
    // Only on arrival and on a change of day — not every minute as `now` ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayKey, startH]);

  const hours = Array.from({ length: endH - startH }, (_, i) => startH + i);

  return (
    <div ref={scroller} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "6px 18px 24px 0" }}>
      <div style={{ position: "relative", height: (endH - startH) * HOUR, marginLeft: 46 }}>
        {hours.map((h) => (
          <div key={h} style={{ position: "absolute", left: 0, right: 0, top: (h - startH) * HOUR, borderTop: "1px solid var(--line)" }}>
            <span style={{ position: "absolute", left: -46, top: -7, width: 38, textAlign: "right", fontSize: 11, color: "var(--muted2)" }}>
              {clock(h, true)}
            </span>
          </div>
        ))}

        {placed.map(({ e, a, b, col, cols }) => {
          const t = tone(e.color);
          const height = Math.max(18, (b - a) * HOUR - 2);
          const short = height < 36;
          const past = e.end.getTime() <= now.getTime();
          return (
            <button key={e.id} title={`${e.summary}\n${span(e)} · ${e.calendarLabel}`}
              onClick={(ev) => onOpen(e, { x: ev.clientX, y: ev.clientY })} style={{
              borderTop: "none", borderRight: "none", borderBottom: "none",
              textAlign: "left", fontFamily: "inherit", cursor: "pointer", display: "block",
              position: "absolute", top: (a - startH) * HOUR + 1, height,
              left: `calc(${(col / cols) * 100}% + 4px)`, width: `calc(${100 / cols}% - 6px)`,
              background: t.bg, color: t.ink, borderRadius: 6, borderLeft: `3px solid ${t.bar}`,
              padding: short ? "1px 7px" : "4px 8px", boxSizing: "border-box", overflow: "hidden",
              fontSize: 12, lineHeight: 1.3, opacity: past ? 0.6 : 1,
            }}>
              {short ? (
                <div style={oneLine}><span style={{ fontWeight: "var(--fw-title)" }}>{e.summary}</span> · {clock(a)}</div>
              ) : (
                <>
                  <div style={{ ...oneLine, fontWeight: "var(--fw-title)" }}>{e.summary}</div>
                  <div style={{ ...oneLine, opacity: 0.85 }}>{span(e)}</div>
                </>
              )}
            </button>
          );
        })}

        {isToday && nowH >= startH && nowH <= endH && (
          <div style={{ position: "absolute", left: -4, right: 0, top: (nowH - startH) * HOUR, borderTop: "2px solid var(--bad)", zIndex: 2, pointerEvents: "none" }}>
            <span style={{ position: "absolute", left: -4, top: -6, width: 10, height: 10, borderRadius: "50%", background: "var(--bad)" }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ---- One event, opened ------------------------------------------------------

const CARD_W = 360;

/**
 * The event, opened beside where you clicked it: when, which calendar, the
 * call link as a button, the location and the notes with their links live.
 *
 * Read-only — an .ics feed is a copy, so there is nothing here to edit, and
 * the feed has no reliable link back to the event in its own calendar app.
 */
function EventCard({ event: e, at, onClose }: { event: DayEvent; at: { x: number; y: number }; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const key = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    const down = (ev: MouseEvent) => { if (ref.current && !ref.current.contains(ev.target as Node)) onClose(); };
    document.addEventListener("keydown", key);
    document.addEventListener("mousedown", down);
    return () => { document.removeEventListener("keydown", key); document.removeEventListener("mousedown", down); };
  }, [onClose]);

  // Beside where you clicked — a block can span the whole grid, so its own
  // edges say nothing about where there's room. Right if it fits, else left;
  // never off-screen.
  const vw = window.innerWidth, vh = window.innerHeight;
  const left = at.x + 14 + CARD_W <= vw - 12
    ? at.x + 14
    : Math.max(12, at.x - 14 - CARD_W);
  const maxH = Math.min(520, vh - 24);
  const top = Math.max(12, Math.min(at.y - 20, vh - maxH - 12));

  const when = e.allDay
    ? `${e.start.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · All day`
    : `${e.start.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · ${span(e)}`;
  const locationIsJoin = !!e.location && !!e.joinUrl && e.location.includes(e.joinUrl);
  const openUrl = e.url && e.url !== e.joinUrl ? e.url : undefined;

  return (
    <div ref={ref} role="dialog" aria-label={e.summary} style={{
      position: "fixed", left, top, width: CARD_W, maxHeight: maxH, zIndex: 60,
      display: "flex", flexDirection: "column", overflow: "hidden",
      background: "var(--raised)", border: "1px solid var(--overlayLine)", borderRadius: 12,
      boxShadow: "var(--shadowOverlay)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "14px 14px 10px" }}>
        <span style={{ width: 12, height: 12, borderRadius: 4, background: e.color, flex: "none", marginTop: 4 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: "var(--fw-title)", lineHeight: 1.3, overflowWrap: "anywhere" }}>{e.summary}</div>
          <div style={{ fontSize: 12.5, color: "var(--ink3)", marginTop: 3 }}>{when}</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 1 }}>
            {e.calendarLabel}{e.alsoOn?.length ? ` · also on ${e.alsoOn.join(", ")}` : ""}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{
          flex: "none", width: 28, height: 28, borderRadius: 999, border: "none", background: "transparent",
          color: "var(--muted)", fontSize: 18, lineHeight: 1, cursor: "pointer",
        }}>×</button>
      </div>

      <div style={{ overflowY: "auto", padding: "0 14px 14px 36px", display: "flex", flexDirection: "column", gap: 10 }}>
        {e.joinUrl && (
          <a href={e.joinUrl} target="_blank" rel="noreferrer noopener" style={{
            alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px",
            borderRadius: 999, background: "var(--accent)", color: "var(--onAccent)", textDecoration: "none",
            fontSize: 12.5, fontWeight: "var(--fw-title)",
          }}>Join {callName(e.joinUrl)} ↗</a>
        )}
        {e.location && !locationIsJoin && (
          <div style={{ fontSize: 12.5, color: "var(--ink2)", overflowWrap: "anywhere" }}>
            <span style={{ color: "var(--muted)" }}>Where </span>{withLinks(e.location, `${e.id}-loc`)}
          </div>
        )}
        {e.description && (
          <div style={{ fontSize: 12.5, color: "var(--ink2)", lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {withLinks(e.description, `${e.id}-notes`)}
          </div>
        )}
        {!e.joinUrl && !e.location && !e.description && (
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>No notes on this event.</div>
        )}
        {openUrl && (
          <a href={openUrl} target="_blank" rel="noreferrer noopener" style={{ fontSize: 12, color: "var(--accent)", textDecoration: "none" }}>
            Open event page ↗
          </a>
        )}
      </div>
    </div>
  );
}

/** "Google Meet", "Zoom", … for the join button; "call" when the host is unknown. */
function callName(url: string): string {
  if (/meet\.google\./i.test(url)) return "Google Meet";
  if (/zoom\.us/i.test(url)) return "Zoom";
  if (/teams\.(microsoft|live)\./i.test(url)) return "Teams";
  if (/webex\.com/i.test(url)) return "Webex";
  return "call";
}

/**
 * Side-by-side columns for overlapping events. Events are walked in start
 * order; each takes the first column free by its start, and a run of mutually
 * overlapping events shares one column count so their widths line up.
 */
function layout<T extends { a: number; b: number }>(rows: T[]): (T & { col: number; cols: number })[] {
  const out: (T & { col: number; cols: number })[] = [];
  let cluster: (T & { col: number; cols: number })[] = [];
  let colEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => { for (const r of cluster) r.cols = colEnds.length; cluster = []; colEnds = []; };
  for (const r of [...rows].sort((x, y) => x.a - y.a || y.b - x.b)) {
    if (cluster.length && r.a >= clusterEnd) flush();
    let col = colEnds.findIndex((end) => end <= r.a);
    if (col === -1) { col = colEnds.length; colEnds.push(r.b); } else colEnds[col] = r.b;
    const placed = { ...r, col, cols: 1 };
    cluster.push(placed);
    out.push(placed);
    clusterEnd = Math.max(clusterEnd, r.b);
  }
  flush();
  return out;
}

// ---- Helpers ----------------------------------------------------------------

/** The current time, ticking each minute so the now-line moves. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function sortItems(items: Item[]): Item[] {
  return [...items].sort((a, b) => {
    const da = scheduledFor(a) || "9999-99-99", db = scheduledFor(b) || "9999-99-99";
    if (da !== db) return da.localeCompare(db);
    return PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
  });
}

/**
 * Items filed under their project: projects A–Z with the Inbox and anything
 * unfiled last, and within each, urgent first, then by title.
 */
function byProject(items: Item[], projectById: Map<string, Project>) {
  const groups = new Map<string, { key: string; name: string; color: string; last: boolean; items: Item[] }>();
  for (const it of items) {
    const p = projectById.get(itemProjectId(it) || "");
    const key = p?.id ?? "";
    let g = groups.get(key);
    if (!g) {
      g = { key: key || "none", name: p?.name ?? "No project", color: p?.color ?? "var(--muted3)", last: !p || p.kind === "inbox", items: [] };
      groups.set(key, g);
    }
    g.items.push(it);
  }
  for (const g of groups.values()) {
    g.items.sort((a, b) => PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority] || a.title.localeCompare(b.title));
  }
  return [...groups.values()].sort((a, b) => Number(a.last) - Number(b.last) || a.name.localeCompare(b.name));
}

/** 9.5 → "9:30 AM"; with `hourOnly`, 13 → "1 PM". */
function clock(h: number, hourOnly = false): string {
  const hh = Math.floor(h) % 24;
  const mm = Math.round((h - Math.floor(h)) * 60);
  const ap = hh < 12 ? "AM" : "PM";
  const h12 = hh % 12 || 12;
  return hourOnly || mm === 0 ? `${h12} ${ap}` : `${h12}:${String(mm).padStart(2, "0")} ${ap}`;
}

function span(e: DayEvent): string {
  const t = (d: Date) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${t(e.start)} – ${t(e.end)}`;
}

function hm(min: number): string {
  if (min < 60) return `${min}m`;
  return min % 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min / 60}h`;
}

const oneLine: CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const navBtn: CSSProperties = {
  padding: "6px 11px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)",
  color: "var(--ink2)", fontSize: 12.5, cursor: "pointer", lineHeight: 1,
};

// The Logged section's rows.
const entryRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "6px 2px", textAlign: "left",
  border: "none", borderBottom: "1px solid var(--line)", background: "transparent", cursor: "pointer", fontFamily: "inherit",
};
const entryMeta: CSSProperties = { ...oneLine, fontSize: 11.5, color: "var(--muted)", marginTop: 1 };
const entryLength: CSSProperties = { flex: "none", fontSize: 12, color: "var(--ink2)" };
const linkBtn: CSSProperties = {
  flex: "none", border: "none", background: "none", padding: "2px 4px", fontSize: 12, color: "var(--muted)",
  cursor: "pointer", fontFamily: "inherit",
};
const logBtn: CSSProperties = {
  flex: "none", padding: "4px 10px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
  border: "1px solid var(--accentLine)", background: "var(--accentTint)", color: "var(--accent)",
};
